import { inject, Injectable } from '@angular/core';
import { CartItem } from '../model/cart-item';
import { CheckoutApiService, CheckoutCustomer, CheckoutData, CheckoutPaymentMethod, CheckoutQuote, CheckoutResult, NativeCheckoutRequest } from './checkout-api.service';

@Injectable()
export class CheckoutStateService {
  private api = inject(CheckoutApiService);
  data: CheckoutData | null = null;
  quote: CheckoutQuote | null = null;
  order: CheckoutResult | null = null;
  paymentMethod: CheckoutPaymentMethod = 'INVOICE';
  customer: CheckoutCustomer = { name: '', email: '', telephone: '', country: 'NO', address1: '', zipCode: '', city: '', acceptEula: false };
  busy = false;
  pending = false;
  error = false;
  private cid = '';
  private attemptId = '';
  private quotedRequest = '';
  private submitted: { request: NativeCheckoutRequest; total: number; itemsSnapshot?: string } | null = null;
  private revision = 0;

  async init(cid: string): Promise<void> {
    this.cid = cid;
    this.attemptId = crypto.randomUUID();
    this.busy = true;
    try {
      const saved = sessionStorage.getItem(this.storageKey());
      if (saved) {
        const stored = JSON.parse(saved);
        if (stored.version === 1 && stored.cid === cid) {
          if (stored.request && Number.isInteger(stored.total)) {
            this.submitted = { request: stored.request, total: stored.total, itemsSnapshot: stored.itemsSnapshot };
            this.customer = stored.request.customer;
            this.paymentMethod = stored.request.paymentMethod;
            this.pending = true;
          } else if (stored.order?.uuid) {
            try {
              this.order = await this.api.order(cid, stored.order.uuid);
            } catch {
              sessionStorage.removeItem(this.storageKey());
            }
          }
        }
      }
      this.data = await this.api.data(cid);
      if (!this.pending) this.paymentMethod = this.data.paymentMethods[0];
    } catch {
      this.error = true;
    } finally {
      this.busy = false;
    }
  }

  invalidate(): void {
    this.revision++;
    this.quote = null;
    this.quotedRequest = '';
  }

  canClear(items: CartItem[]): boolean {
    return this.submitted?.itemsSnapshot === JSON.stringify(items);
  }

  clearOrder(): void {
    this.order = null;
    sessionStorage.removeItem(this.storageKey());
  }

  async validate(items: CartItem[]): Promise<void> {
    const revision = ++this.revision;
    this.quote = null;
    this.error = false;
    this.busy = true;
    try {
      const request = this.api.buildRequest(items, this.customer, this.paymentMethod, this.attemptId);
      const quote = await this.api.validate(this.cid, request);
      if (revision === this.revision) {
        this.quote = quote;
        this.quotedRequest = JSON.stringify(request);
      }
    } catch {
      if (revision === this.revision) this.error = true;
    } finally {
      this.busy = false;
    }
  }

  async submit(items: CartItem[]): Promise<CheckoutResult | null> {
    if (this.busy || this.order || (!this.quote && !this.pending)) return null;
    this.busy = true;
    this.error = false;
    try {
      if (!this.submitted) {
        const request = this.api.buildRequest(items, this.customer, this.paymentMethod, this.attemptId);
        if (JSON.stringify(request) !== this.quotedRequest) {
          this.invalidate();
          return null;
        }
        const submitted = { request, total: this.quote!.total, itemsSnapshot: JSON.stringify(items) };
        sessionStorage.setItem(this.storageKey(), JSON.stringify({ version: 1, cid: this.cid, ...submitted }));
        this.submitted = submitted;
      }
      this.pending = true;
      this.order = await this.api.placeOrder(this.cid, this.submitted.request, this.submitted.total);
      sessionStorage.setItem(this.storageKey(), JSON.stringify({
        version: 1, cid: this.cid, order: { uuid: this.order.uuid, bookingId: this.order.bookingId }
      }));
      return this.order;
    } catch (error) {
      this.error = true;
      if (this.order) return this.order;
      if ((error as { rejected?: boolean })?.rejected) {
        sessionStorage.removeItem(this.storageKey());
        this.submitted = null;
        this.pending = false;
        if ((error as { code?: string })?.code === 'attempt_conflict') this.attemptId = crypto.randomUUID();
        this.invalidate();
      }
      return null;
    } finally {
      this.busy = false;
    }
  }

  private storageKey(): string {
    return `checkout-next:v1:${this.cid}`;
  }
}
